// Lightweight fuzzy matching for active users. Used when the server-side
// substring search returns 0 results — voice transcription often produces
// near-misses ("Joanne" for "Joan", "Niso" for "Nizam") and we don't want
// the model to give up just because the literal substring failed.
//
// Strategy: tokenize both the query and each candidate's full name into
// lowercased words, then score each candidate by how well its tokens match
// the query tokens. We use a hybrid score:
//   - perfect substring match  → +10
//   - prefix match              → +5  (e.g. "Joa" → "Joan")
//   - Levenshtein <= 2          → +2  (e.g. "Joanne" → "Joan")
// Email is also considered with a smaller weight.

function levenshtein(a, b) {
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;
    const dp = new Array(b.length + 1);
    for (let j = 0; j <= b.length; j++) dp[j] = j;
    for (let i = 1; i <= a.length; i++) {
        let prev = i - 1;
        dp[0] = i;
        for (let j = 1; j <= b.length; j++) {
            const tmp = dp[j];
            if (a[i - 1] === b[j - 1]) {
                dp[j] = prev;
            } else {
                dp[j] = 1 + Math.min(prev, dp[j], dp[j - 1]);
            }
            prev = tmp;
        }
    }
    return dp[b.length];
}

function scoreToken(qToken, candToken) {
    if (!qToken || !candToken) return 0;
    if (qToken === candToken) return 10;
    if (candToken.includes(qToken) || qToken.includes(candToken)) return 8;
    if (candToken.startsWith(qToken) || qToken.startsWith(candToken)) return 5;
    // Only consider Levenshtein for tokens of similar length — avoids
    // matching "Bob" against "Joanne" via 5 edits.
    if (Math.abs(qToken.length - candToken.length) <= 2) {
        const d = levenshtein(qToken, candToken);
        if (d <= 1) return 4;
        if (d <= 2) return 2;
    }
    return 0;
}

/**
 * Rank candidates against a query and return the top N matches.
 *
 * @param {string} query  Free-text user query (e.g. "Joanne", "Joan I").
 * @param {Array<{id, first_name, last_name, email}>} users  Active user list.
 * @param {object} [options]
 * @param {number} [options.limit=5]
 * @param {number} [options.minScore=4]   Below this score, candidates are dropped.
 * @returns {Array<{id, first_name, last_name, email, score}>}
 */
export function fuzzyMatchUsers(query, users, options = {}) {
    const { limit = 5, minScore = 4 } = options;
    if (!query || !Array.isArray(users) || users.length === 0) return [];

    const qTokens = String(query)
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean);
    if (qTokens.length === 0) return [];

    const ranked = users.map((u) => {
        const candTokens = [
            (u.first_name || "").toLowerCase(),
            (u.last_name || "").toLowerCase()
        ].filter(Boolean);
        const emailLocal = (u.email || "").split("@")[0].toLowerCase();
        if (emailLocal) candTokens.push(emailLocal);

        // For each query token, take the BEST score across candidate tokens.
        // Sum across query tokens — a query like "Joan Ilari" matching both
        // "joan" and "ilari" scores higher than just one match.
        const score = qTokens.reduce((acc, qt) => {
            let best = 0;
            for (const ct of candTokens) {
                const s = scoreToken(qt, ct);
                if (s > best) best = s;
            }
            return acc + best;
        }, 0);

        return { ...u, score };
    });

    return ranked
        .filter((r) => r.score >= minScore)
        .sort((a, b) => b.score - a.score)
        .slice(0, limit)
        .map((r) => ({
            id: r.id,
            first_name: r.first_name,
            last_name: r.last_name,
            email: r.email,
            name: `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.email,
            score: r.score
        }));
}
